import { create } from 'zustand';
import { Usuario, LoginForm } from '../types';
import api from '../utils/api';

interface AuthState {
  user: Usuario | null;
  token: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
}

interface AuthStore extends AuthState {
  login: (credentials: LoginForm) => Promise<void>;
  logout: () => void;
  setUser: (user: Usuario) => void;
  setToken: (token: string) => void;
  clearAuth: () => void;
}

/** Chave usada pela versão anterior do app (zustand persist). */
const LEGACY_STORAGE_KEY = 'motorista-auth-storage';

const getInitialState = (): AuthState => {
  const token = localStorage.getItem('motorista_token');
  const userStr = localStorage.getItem('motorista_user');

  return {
    token: token || null,
    user: userStr ? JSON.parse(userStr) : null,
    isAuthenticated: !!token,
    isLoading: false,
  };
};

export const useAuthStore = create<AuthStore>((set, get) => ({
  ...getInitialState(),

  login: async (credentials: LoginForm) => {
    set({ isLoading: true });
    try {
      const response = await api.post<{ token: string; user: Usuario }>(
        '/auth/login',
        credentials
      );
      const { token, user } = response.data;

      if (user.funcao !== 'motorista') {
        throw new Error(
          'Acesso negado. Apenas motoristas podem usar este app.'
        );
      }

      set({
        token,
        user,
        isAuthenticated: true,
        isLoading: false,
      });
      localStorage.setItem('motorista_token', token);
      localStorage.setItem('motorista_user', JSON.stringify(user));
    } catch (error: unknown) {
      set({ isLoading: false });
      const err = error as { response?: { data?: { error?: string } }; message?: string };
      const message =
        err.response?.data?.error || err.message || 'Erro ao fazer login';
      throw new Error(message);
    }
  },

  logout: () => {
    get().clearAuth();
  },

  setUser: (user: Usuario) => {
    set({ user, isAuthenticated: true });
    localStorage.setItem('motorista_user', JSON.stringify(user));
  },

  setToken: (token: string) => {
    set({ token, isAuthenticated: true });
    localStorage.setItem('motorista_token', token);
  },

  clearAuth: () => {
    set({
      user: null,
      token: null,
      isAuthenticated: false,
      isLoading: false,
    });
    localStorage.removeItem('motorista_token');
    localStorage.removeItem('motorista_user');
    localStorage.removeItem(LEGACY_STORAGE_KEY);
  },
}));
